import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { CoreText } from '@ingenico/react-ui/core-text';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { DateTime } from '@ingenico/react-ui/date-time';
import { SvgAsset } from '@ingenico/react-ui/svg-asset';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@mui/material';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ReadonlyLabelExtraData,
  ReadonlyLabelType,
  ReadonlyLabelWithIconData,
  ReadonlyLabelWithIconMetadata,
  ReadonlyLabelWithIconProps,
} from './readonlyLabelWithIcon.model';
import styles from './readonlyLabelWithIcon.module.scss';

export function ReadonlyLabelWithIcon({ data, componentClassName }: ReadonlyLabelWithIconProps): ReactNode {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE]);
  const getValueAsText = useCallback(
    (displayedValue: string, type?: ReadonlyLabelType, extraData?: ReadonlyLabelExtraData) => {
      return type === 'date' ? (
        <DateTime
          customClassName={styles.dateLabel}
          instant={displayedValue}
          timezone={extraData?.zoneInfo}
          userDateTimeFormat={`${extraData?.dateFormat}`}
        />
      ) : (
        <CoreText
          className={classNames(componentClassName?.value, styles.value, {
            [`${componentClassName?.value}`]: !!componentClassName?.value,
          })}
          numRows={2}
          tooltipTitle={displayedValue}
          noWrap
        >
          {displayedValue}
        </CoreText>
      );
    },
    [componentClassName],
  );

  const getValueWithLabel = useCallback(
    (displayedValue: ReadonlyLabelWithIconMetadata, key?: string) => (
      <div
        className={classNames(styles.valueWithLabel, {
          [styles.valueWithLabel__noGap]: !displayedValue.label,
        })}
        key={key}
      >
        <CoreTypography
          customClassName={classNames(styles.label, { [`${componentClassName?.label}`]: !!componentClassName?.label })}
          variant={CoreTypographyVariant.Body02Regular}
        >
          {t(displayedValue.label)}
        </CoreTypography>
        {getValueAsText(displayedValue.value, displayedValue.type, displayedValue.extraData)}
      </div>
    ),
    [componentClassName?.label, getValueAsText, t],
  );

  const getValueInList = useCallback(
    (displayedListValue: (ReadonlyLabelWithIconMetadata | string)[] | ReadonlyLabelWithIconMetadata) => {
      return Array.isArray(displayedListValue)
        ? displayedListValue.map((displayedValue) =>
            typeof displayedValue === 'string'
              ? getValueAsText(displayedValue)
              : getValueWithLabel(displayedValue, `${displayedValue.label}-${displayedValue.value}`),
          )
        : getValueWithLabel(displayedListValue);
    },
    [getValueAsText, getValueWithLabel],
  );

  const getValue = useCallback(
    (displayedValue: ReadonlyLabelWithIconData['value']) =>
      typeof displayedValue === 'string' ? getValueAsText(displayedValue) : getValueInList(displayedValue),
    [getValueInList, getValueAsText],
  );

  return (
    <div className={styles.container}>
      {data.map(({ key, icon, value, title, valueAfterTitle, tooltip }) => (
        <div className={styles.elementsWrapper} key={key}>
          {icon ? (
            <div className={styles.iconContainer}>
              <SvgAsset className={styles.icon} image={icon} />
            </div>
          ) : undefined}
          <div className={classNames(styles.labelsContainer, componentClassName?.labelsContainer)}>
            {title || valueAfterTitle || tooltip ? (
              <div className={styles.titlesContainer}>
                <CoreTypography customClassName={styles.label} variant={CoreTypographyVariant.Body02Regular}>
                  {t(title)}
                </CoreTypography>
                {valueAfterTitle ? getValue(valueAfterTitle) : undefined}
                {tooltip ? (
                  <Tooltip title={t(tooltip)}>
                    <InfoIcon className={styles.tooltipIcon} />
                  </Tooltip>
                ) : undefined}
              </div>
            ) : undefined}
            {getValue(value)}
          </div>
        </div>
      ))}
    </div>
  );
}
